import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Mail, X, Share2, Check, Send } from "lucide-react";

export default function ShareModal({ open, onClose, scope, defaultTitle }) {
  // scope: { report_id, month, report_name }
  const [expires, setExpires] = useState(7);
  const [title, setTitle] = useState(defaultTitle || "");
  const [includePdf, setIncludePdf] = useState(true);
  const [includeExcel, setIncludeExcel] = useState(true);
  const [creating, setCreating] = useState(false);
  const [link, setLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [emailMode, setEmailMode] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [sending, setSending] = useState(false);

  if (!open) return null;

  const create = async () => {
    if (!includePdf && !includeExcel) {
      toast.error("Sélectionnez au moins un type de fichier");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/share/create", {
        report_id: scope?.report_id || null,
        month: scope?.month || null,
        title: title || null,
        expires_days: Number(expires),
        types: [includePdf && "pdf", includeExcel && "excel"].filter(Boolean),
      });
      const url = `${window.location.origin}/s/${data.token}`;
      setLink({ url, ...data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Création du lien impossible");
    } finally {
      setCreating(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Lien copié");
    } catch {
      toast.error("Copie impossible");
    }
  };

  const mailto = () => {
    const subject = encodeURIComponent(title || "Compte de dépenses" + (scope?.report_name ? ` — ${scope.report_name}` : scope?.month ? ` — ${scope.month}` : ""));
    const bodyText = `Bonjour,

Voici mon compte de dépenses. Vous pouvez télécharger le PDF et le Excel depuis ce lien :

${link.url}

Ce lien restera actif ${expires} jour(s).

Merci !`;
    const body = encodeURIComponent(bodyText);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const sendEmail = async () => {
    const emails = emailTo.split(",").map((e) => e.trim()).filter(Boolean);
    if (!emails.length) { toast.error("Entrez au moins un destinataire"); return; }
    setSending(true);
    try {
      await api.post("/email/send-report", {
        to: emails,
        subject: emailSubject || title || "Compte de dépenses",
        message: emailMessage,
        report_id: scope?.report_id || null,
        month: scope?.month || null,
        include_pdf: includePdf,
        include_excel: includeExcel,
        share_url: link?.url || null,
      });
      toast.success(`Courriel envoyé à ${emails.length} destinataire(s)`);
      setEmailMode(false);
      setEmailTo("");
      setEmailSubject("");
      setEmailMessage("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible");
    } finally {
      setSending(false);
    }
  };

  const reset = () => { setLink(null); setCopied(false); setEmailMode(false); };
  const close = () => { reset(); onClose(); };

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="share-modal">
      <div className="bg-white border-2 border-[#0B0F19] w-full max-w-lg shadow-[8px_8px_0px_0px_rgba(0,0,0,0.1)]">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div>
            <p className="overline">Partager</p>
            <div className="font-heading font-black text-2xl tracking-tighter flex items-center gap-2">
              <Share2 size={20} /> Lien public
            </div>
          </div>
          <button onClick={close} className="btn-ghost" data-testid="share-close-button"><X size={16} /></button>
        </div>

        {!link ? (
          <div className="p-5 space-y-4">
            <div>
              <label className="ra-label">Titre (optionnel)</label>
              <input
                className="ra-input"
                placeholder="Ex: Compte de dépenses Novembre 2025"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                data-testid="share-title-input"
              />
            </div>
            <div>
              <label className="ra-label">Expiration</label>
              <div className="flex gap-2">
                {[7, 30, 90].map((d) => (
                  <button
                    key={d}
                    onClick={() => setExpires(d)}
                    className={`flex-1 py-2 border text-sm font-semibold transition-colors ${expires === d ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white border-gray-300 text-gray-700 hover:border-[#0B0F19]"}`}
                    data-testid={`share-expires-${d}`}
                  >
                    {d} jours
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="ra-label">Fichiers inclus</label>
              <div className="flex flex-col gap-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={includePdf} onChange={(e) => setIncludePdf(e.target.checked)} data-testid="share-include-pdf" />
                  <span className="text-sm">PDF (factures triées par date)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={includeExcel} onChange={(e) => setIncludeExcel(e.target.checked)} data-testid="share-include-excel" />
                  <span className="text-sm">Excel (compte de dépenses Riva-Avir)</span>
                </label>
              </div>
            </div>
            <div className="text-xs text-gray-500 bg-gray-50 p-3 border border-gray-200">
              Le destinataire pourra télécharger les fichiers sans compte. Les montants, participants et photos de factures seront visibles.
            </div>
          </div>
        ) : (
          <div className="p-5 space-y-4" data-testid="share-link-result">
            <div>
              <label className="ra-label">Lien de partage</label>
              <div className="flex gap-2">
                <input
                  className="ra-input font-mono text-xs"
                  value={link.url}
                  readOnly
                  onFocus={(e) => e.target.select()}
                  data-testid="share-link-input"
                />
                <button onClick={copy} className="btn-secondary shrink-0" data-testid="share-copy-button">
                  {copied ? <><Check size={14} /> Copié</> : <><Copy size={14} /> Copier</>}
                </button>
              </div>
            </div>
            <div className="text-xs text-gray-500">
              Expire le <span className="num font-semibold">{String(link.expires_at).slice(0, 10)}</span>
            </div>
            <div className="pt-2 border-t border-gray-100">
              {!emailMode ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <button onClick={mailto} className="btn-secondary justify-center" data-testid="share-mailto-button">
                    <Mail size={14} /> Messagerie native
                  </button>
                  <button onClick={() => setEmailMode(true)} className="btn-primary justify-center" data-testid="share-email-form-button">
                    <Send size={14} /> Envoyer avec pièces jointes
                  </button>
                </div>
              ) : (
                <div className="space-y-3 bg-gray-50 p-4 border border-gray-200" data-testid="email-form">
                  <div>
                    <label className="ra-label">Destinataire(s)</label>
                    <input className="ra-input" placeholder="a@exemple.com, b@exemple.com" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} data-testid="email-to-input" />
                  </div>
                  <div>
                    <label className="ra-label">Sujet</label>
                    <input className="ra-input" placeholder={title || "Compte de dépenses"} value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} data-testid="email-subject-input" />
                  </div>
                  <div>
                    <label className="ra-label">Message (optionnel)</label>
                    <textarea className="ra-input" rows={3} placeholder="Ajouter un mot au destinataire..." value={emailMessage} onChange={(e) => setEmailMessage(e.target.value)} data-testid="email-message-input" />
                  </div>
                  <p className="text-xs text-gray-500">PDF + Excel seront joints selon votre sélection ci-dessus. En mode test Resend (sans domaine vérifié), utilisez <span className="font-mono">delivered@resend.dev</span> ou votre propre adresse Resend.</p>
                  <div className="flex gap-2">
                    <button onClick={sendEmail} disabled={sending} className="btn-primary flex-1 justify-center" data-testid="email-send-button">
                      {sending ? "Envoi..." : <><Send size={14} /> Envoyer</>}
                    </button>
                    <button onClick={() => setEmailMode(false)} className="btn-secondary">Annuler</button>
                  </div>
                </div>
              )}
              {!emailMode && <p className="text-xs text-gray-400 mt-2 text-center">Le lien sera inclus automatiquement.</p>}
            </div>
          </div>
        )}

        <div className="p-5 border-t border-gray-200 flex gap-2 justify-end">
          {!link ? (
            <>
              <button onClick={close} className="btn-secondary">Annuler</button>
              <button onClick={create} disabled={creating} className="btn-primary" data-testid="share-create-button">
                {creating ? "Création..." : "Générer le lien"}
              </button>
            </>
          ) : (
            <>
              <button onClick={reset} className="btn-secondary" data-testid="share-new-button">Nouveau lien</button>
              <button onClick={close} className="btn-primary">Fermer</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
